import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { FormControl, FormGroup } from '@angular/forms';
import SketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import { MapService } from "../../view/services/map.service";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import * as geometryEngineAsync from "@arcgis/core/geometry/geometryEngineAsync";
import { saveAs } from 'file-saver';
import { FeatureCollection, Feature, Geometry } from 'geojson';
import JSZip from 'jszip';
import { environment } from "../../../environments/environment";


interface CustomFeatureCollection extends FeatureCollection {
  name: string;
}

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css']
})
export class SelectComponent implements OnInit, OnDestroy {

  selectForm = new FormGroup({
    selectionForm: new FormControl('') as any
  });

  sketchGraphic = new GraphicsLayer();
  resultSelect: any[] = [];
  clickButton: boolean = false;

  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer, public mapService: MapService) {
    iconRegistry.addSvgIcon("circle", sanitizer.bypassSecurityTrustResourceUrl("assets/img/SVG/selectbyCircle.svg"));
    iconRegistry.addSvgIcon("rectangle", sanitizer.bypassSecurityTrustResourceUrl("assets/img/SVG/selectbyRectangle.svg"));
    iconRegistry.addSvgIcon("polygon", sanitizer.bypassSecurityTrustResourceUrl("assets/img/SVG/selectbyPolygon.svg"));
    iconRegistry.addSvgIcon("point", sanitizer.bypassSecurityTrustResourceUrl("assets/img/SVG/selectbyPoint.svg"));
    iconRegistry.addSvgIcon("polyline", sanitizer.bypassSecurityTrustResourceUrl("assets/img/SVG/selectbyLine.svg"));
  }

  ngOnDestroy(): void {
    this.clearResults();
  }

  ngOnInit(): void {}
  

  startSelect() {
    const sketch = new SketchViewModel({
      view: this.mapService.getViewMap(),
      layer: this.sketchGraphic,
    });
    sketch.create(this.selectForm.value.selectionForm);
    sketch.on("create", async (event) => {
      if (event.state === "complete") {
        const geometries = this.sketchGraphic.graphics.map(function (graphic) {
          return graphic.geometry;
        });
        const queryGeometry = await geometryEngineAsync.union(
          geometries.toArray()
        );
        this.selectFeatures(queryGeometry);
        this.sketchGraphic.remove(event.graphic);
      }
    });
  }

  selectFeatures(geometry) {
    let layerGroup;
    this.mapService.getViewMap().layerViews.forEach((layer) => {
      if (layer.layer.type == "group") {
        layerGroup = layer as any;
      }
      layerGroup.layerViews.items.forEach(layerView => {
        if (layerView.layer.type == "feature") {
          layerView.layer.outFields = ["*"];
          layerView.layer.popupTemplate.overwriteActions = true;
          layerView.queryFeatures({
            geometry: geometry,
            returnGeometry: true,
            returnQueryGeometry: false,
            outFields: ["*"],
          })
            .then((result) => {
              if (result.features.length > 0) {
                result.highlight = layerView.highlight(result.features);
                result.check = true;
                result.layerView = layerView;
                this.resultSelect.push(result);
              }
            });
        }
      });
    });
  }

  changeCheck(group) {
    this.clickButton = true;
    if (group.check) {
      group.highlight.remove();
    } else {
      group.layerView.highlight(group.features);
    }
    group.check = !group.check;

  }

  viewAttributesFeature(feature) {
    if (this.mapService.getViewMap().popup.visible) {
      this.mapService.getViewMap().popup.visible = false;
    }
    this.mapService.getViewMap().popup.dockEnabled = true;
    this.mapService.getViewMap().popup.spinnerEnabled = true;
    this.mapService.getViewMap().popup.collapseEnabled = false;
    this.mapService.getViewMap().popup.highlightEnabled = false;
    this.mapService.getViewMap().popup.dockOptions = {
      buttonEnabled: false,
      position: 'bottom-center'
    };
    this.mapService.getViewMap().popup.open({
      features: [feature]
    });
  }

  download = environment.modules.allowdownload;

  downloadGeoJSON() {
    const zip = new JSZip();
    const layerNames = this.getUniqueLayerNames();
  
    layerNames.forEach(layerName => {
      const fileName = layerName + '.geojson';
      const featureCollection = this.collectSelectedFeaturesAsFeatureCollection(fileName, layerName);
      const geoJSONData = JSON.stringify(featureCollection);
      zip.file(fileName, geoJSONData);
    });
  
    zip.generateAsync({type:"blob"})
      .then(function(content) {
        saveAs(content, "layers_WGS84.zip");
      });
  }
  
  getUniqueLayerNames(): string[] {
    const layerNames = [];
    this.resultSelect.forEach(group => {
      if (layerNames.indexOf(group.layerView.layer.title) === -1) {
        layerNames.push(group.layerView.layer.title);
      }
    });
    return layerNames;
  }
  
  collectSelectedFeaturesAsFeatureCollection(fileName: string, layerName: string) {
    const selectedFeatures = [];
    this.resultSelect.forEach(group => {
      if (group.layerView.layer.title === layerName) {
        group.features.forEach(feature => {
          let coordinates = [];
          const geometryType = feature.geometry.type.charAt(0).toUpperCase() + feature.geometry.type.slice(1); 
  
          if (geometryType === 'Point') {
            coordinates = [feature.geometry.x, feature.geometry.y]; 
          } else if (geometryType === 'Polygon') {
            const rings = feature.geometry.rings.map(ring => {
              ring.push(ring[0]);
              return ring;
            });
            coordinates = rings;
          } else if (geometryType === 'LineString') {
            coordinates = [feature.geometry.paths.map(path => {
              return path.map(point => [point[0], point[1]]);
            })];
          }
  
          const properties = feature.attributes; 
  
          const selectedFeature = {
            type: 'Feature',
            geometry: {
              type: geometryType, 
              coordinates: coordinates
            },
            properties: properties
          };
          selectedFeatures.push(selectedFeature);
        });
      }
    });
  
    const featureCollection: CustomFeatureCollection = {
      type: 'FeatureCollection',
      features: selectedFeatures,
      name: fileName 
    };
  
    return featureCollection;
  }
  

  removeFeature(feature, group) {
    if (group.features.length === 1) {
      this.removeGroup(group)
    } else {
      group.features = group.features.filter(item => item !== feature);
      group.highlight.remove();
      group.highlight = group.layerView.highlight(group.features);
    }

  }

  removeGroup(group) {
    group.highlight.remove();
    this.resultSelect = this.resultSelect.filter(item => item !== group)
  }

  clearResults() {
    this.resultSelect.forEach(group => {
      group.highlight.remove();
    });
    this.resultSelect = [];
  }

}
